import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { path, pathOr } from "ramda";
import { css } from "emotion";
import {
	getDynamicFormCategorieRules,
	getDynamicFormCategorieSchemas
} from "../../shared/requests-api";
import { MaxModal, Heading, Button } from "@maxmilhas/hangar-components-atomic/public";
import * as Components from "./components";
import * as cs from "./style";
import ModalError from "./components/ModalError";

export const DynamicForm = ({
	open,
	title,
	showTitle,
	headerImage,
	modalWidth,
	ruleId,
	contextToken,
	correlationId,
	onClose
}) => {
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [rules, setRules] = useState(null);
	const [schema, setSchema] = useState(null);
	const [data, setData] = useState(null);
	const [modalError, setModalError] = useState({ open: false });

	const buttonIsDisabled = useMemo(() => {
		if (schema && data) {
			const disabledRules = schema.required ? [...schema.required] : [];

			Object.entries(schema.properties).forEach(property => {
				const [key, value] = property;
				if (path(["component", "required"], value)) disabledRules.push(key);
			});

			let flag = false;

			disabledRules.forEach(_rule => {
				if (!data[_rule] || (data[_rule] && data[_rule].length === 0)) flag = true;
			});

			return flag;
		} else return false;
	}, [data, schema]);

	const actionButtonText = useMemo(() => {
		if (rules && rules.lastStep) return "Fechar";
		if (rules && rules.total - rules.step === 1) return "Finalizar";
		return "Próximo";
	}, [rules]);

	const stepUiProps = useMemo(() => {
		let uiProps = {
			actionButtonText,
			title,
			showTitle,
			modalWidth,
			headerImage
		};

		if (path(["metadata", "uiProps"], rules)) {
			uiProps = { ...uiProps, ...rules.metadata.uiProps };
		}

		return uiProps;
	}, [rules, actionButtonText, title, modalWidth, headerImage]);

	const getRules = async ({ id, params }) => {
		try {
			const _rules = await getDynamicFormCategorieRules(id, params, contextToken, correlationId);

			if (path(["data", "errors"], _rules)) {
				setModalError({ open: true, messages: _rules.data.errors });
			} else if (path(["data", "metadata", "redirectToRule"], _rules)) {
				getRules({
					params: { viewState: path(["data", "viewState"], _rules) },
					id: path(["data", "metadata", "redirectToRule"], _rules)
				});
			} else {
				setRules({ ...pathOr({}, ["data"], _rules), id });
			}
		} catch (error) {
			setModalError({ open: true });
		}
	};

	useEffect(() => {
		getRules({ params: {}, id: ruleId });
	}, []);

	useEffect(() => {
		const checkComponent = componentName => {
			switch (componentName) {
				case "RawHtml":
					return "children";
				case "InformationText":
					return "children";
				case "AcceptanceTerms":
					return "children";
				case "CheckboxGroup":
					return "options";
				case "RadioGroup":
					return "options";
				case "OffersList":
					return "data";
				case "Redirect":
					return "url";
				default:
					return "value";
			}
		};

		const getSchema = async schemaId => {
			try {
				const _schema = await getDynamicFormCategorieSchemas(schemaId, correlationId);
				const _data = {};

				Object.entries(_schema.data.properties).forEach(field => {
					const [key, values] = field;

					if (path(["data", key], rules)) {
						if (path(["component", "name"], values)) {
							values.component.props = {
								...values.component.props,
								[checkComponent(values.component.name)]: rules.data[key]
							};
						} else {
							values.value = rules.data[key];
						}
					}

					_data[key] = null;
				});

				setData(_data);
				setSchema(_schema.data);
				setLoading(false);
			} catch (error) {
				setModalError({ open: true });
			} finally {
				setActionLoading(false);
			}
		};

		if (rules && rules.schemaId) {
			getSchema(rules.schemaId);
		}
	}, [rules]);

	useEffect(() => {
		if (schema) {
			window.scrollTo(0, 0);
		}
	}, [schema]);

	const handleCancel = () => {
		if (path(["lastStep"], rules) && path(["total"], rules) !== 1) {
			window.location.pathname = "/cliente/minhas-viagens-light";
		} else {
			onClose();
		}
	};

	const handleModalErrorClose = () => {
		setActionLoading(false);
		if (loading) handleCancel();
		setModalError({ open: false });
	};

	const handleStep = () => {
		if (path(["lastStep"], rules)) {
			handleCancel();
		} else {
			setActionLoading(true);

			const obj = {};

			Object.keys(data).forEach(key => {
				if (data[key] && schema.properties[key].component.name !== "AcceptanceTerms") {
					obj[key] = data[key];
				}
			});

			getRules({
				id: (rules || {}).id,
				params: {
					viewState: (rules || {}).viewState,
					data: obj
				}
			});
		}
	};

	const getCompnentValue = (event, componentName) => {
		switch (componentName) {
			case "AcceptanceTerms":
				return event.target.checked;
			case "CheckboxGroup":
				return event;
			case "RadioGroup":
				return event;
			case "AutocompleteAirports":
				return event;
			case "OffersList":
				return event;
			case "Input":
				return event;
			default:
				return event.target.value;
		}
	};

	const handleComponentInput = (event, componentName, key) => {
		const componentValue = getCompnentValue(event, componentName);

		setData({
			...data,
			[key]: componentValue
		});
	};

	return (
		<MaxModal
			open={open}
			onClose={actionLoading ? () => undefined : handleCancel}
			closeOnOverlayClick={false}
			focusTrapped={false}
		>
			<div
				className={css`
					${cs.modalCss(stepUiProps.modalWidth)}
				`}
			>
				{stepUiProps.headerImage && (
					<img className="modal-thumbnail" src={stepUiProps.headerImage} />
				)}

				{stepUiProps.showTitle && stepUiProps.title && (
					<Heading tag="h2" className="modal-title">
						{stepUiProps.title}
					</Heading>
				)}

				{loading && (
					<div className="modal-loader-container">
						<i className="loader loader-fetching center-block" />
					</div>
				)}

				{!loading && (
					<>
						{Object.entries(schema.properties).map(field => {
							const [key, value] = field;

							if (value.component) {
								const Component = Components[value.component.name];

								return (
									<div className="modal-field" key={key}>
										<Component
											id={key}
											name={key}
											onChange={e => handleComponentInput(e, value.component.name, key)}
											value={
												path([key], data) ||
												pathOr(undefined, ["component", "props", "value"], value)
											}
											{...pathOr({}, ["component", "props"], value)}
										>
											{path(["component", "props", "children"], value)}
										</Component>
									</div>
								);
							} else {
								return undefined;
							}
						})}

						<div className="modal-buttons">
							<div
								className={css`
									${cs.modalSteps(rules.step, rules.total)}
								`}
							/>

							<Button
								onClick={handleStep}
								disabled={buttonIsDisabled || actionLoading}
								style={{ padding: actionLoading ? "4px 24px" : "12px 24px" }}
							>
								{actionLoading ? (
									<i className="loader loader-fetching center-block" />
								) : (
									stepUiProps.actionButtonText
								)}
							</Button>
						</div>
					</>
				)}

				{modalError.open && (
					<ModalError
						open={modalError.open}
						onClose={handleModalErrorClose}
						correlationId={correlationId}
						messages={modalError.messages}
					/>
				)}
			</div>
		</MaxModal>
	);
};

DynamicForm.propTypes = {
	open: PropTypes.bool.isRequired,
	title: PropTypes.string,
	showTitle: PropTypes.bool,
	ruleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	contextToken: PropTypes.string.isRequired,
	correlationId: PropTypes.string,
	headerImage: PropTypes.string,
	modalWidth: PropTypes.string,
	onClose: PropTypes.func
};

DynamicForm.defaultProps = {
	title: "",
	showTitle: true,
	headerImage: "https://website-hangar.s3.amazonaws.com/2020/02/14/protocol-cover.svg",
	modalWidth: "600px",
	onClose: () => undefined
};

export default DynamicForm;
